import { Injectable } from '@angular/core';
import moment from 'moment';
import { DashboardTile } from 'src/app/dashboard/dashboardStatCards';
import { PendingPaymentLineItem } from '../../dashboard/pending-payment-list/pending-payment-list.component';
import { Delivery } from '../../models';
import { Booking } from '../../models/booking';
import { BookingStatus } from '../../models/booking-status';
import { CombinedCalendarDataPayload } from '../../models/combined-calendar';
import { ApiClient } from '../api.client';

@Injectable()
export class DashboardService {
  constructor(private http: ApiClient) {}

  getDashboardStats() {
    return this.http.get<any>('dashboard');
  }

  getCombinedCalendarData() {
    return this.http.get<CombinedCalendarDataPayload>('dashboard/combined');
  }

  getCustomDashboardTiles() {
    return this.http.get<DashboardTile[]>('dashboard/custom-dashboard-tiles');
  }

  filterReturningToday(items: ActiveDocket[]) {
    const start = moment().startOf('day').toDate();
    const end = moment().endOf('day').toDate();

    return items.filter((item) => {
      if (!item.estimated_off_hire) {
        return false;
      }
      const returnTime = new Date(item.estimated_off_hire).getTime();
      return returnTime > start.getTime() && returnTime < end.getTime();
    });
  }

  filterOverdue(items: ActiveDocket[]) {
    let now = new Date().getTime();
    return items.filter(
      (item) =>
        Boolean(item.estimated_off_hire) &&
        new Date(item.estimated_off_hire).getTime() < now
    );
  }

  filterUnknown(items: ActiveDocket[]) {
    return items.filter((item) => !item.estimated_off_hire);
  }

  bookingsFilterRequests(bookings: Booking[]) {
    return bookings.filter(
      (booking) => booking.status === BookingStatus.REQUEST
    );
  }

  bookingsFilterStockWarnings(bookings: Booking[]) {
    return bookings.filter(
      (booking) => booking.items.filter((item) => item.conflict_level).length
    );
  }

  bookingsFilterAwaiting(bookings: Booking[]) {
    return bookings.filter(
      (booking) => booking.status === BookingStatus.QUOTATION
    );
  }

  bookingsFilterConfirmed(bookings: Booking[]) {
    return bookings.filter(
      (booking) => booking.status === BookingStatus.CUSTOMER_ACCEPTED
    );
  }

  bookingsFilterOverdue(bookings: Booking[]) {
    const now = new Date().getTime();
    return bookings.filter((booking) => {
      return booking.items.some(
        (item) => now > new Date(item.on_hire).getTime()
      );
    });
  }

  bookingsFilterToday(bookings: Booking[]) {
    const start = moment().startOf('day').toDate().getTime();
    const end = moment().endOf('day').toDate().getTime();
    return bookings.filter((booking) => {
      const firstOn = new Date(booking.items[0].on_hire).getTime();
      return firstOn > start && firstOn < end;
    });
  }

  bookingsFilterThisWeek(bookings: Booking[]) {
    const start = moment().startOf('day').toDate().getTime();
    const end = moment().endOf('week').toDate().getTime();
    return bookings.filter((booking) => {
      const firstOn = new Date(booking.items[0].on_hire).getTime();
      return firstOn > start && firstOn < end;
    });
  }

  deliveriesFilterOverdue(deliveries: Delivery[]) {
    const now = new Date().getTime();
    return deliveries.filter(
      (delivery) => now > new Date(delivery.due).getTime()
    );
  }

  deliveriesFilterToday(deliveries: Delivery[]) {
    const start = moment().startOf('day').toDate().getTime();
    const end = moment().endOf('day').toDate().getTime();
    return deliveries.filter((delivery) => {
      const firstOn = new Date(delivery.due).getTime();
      return firstOn > start && firstOn < end;
    });
  }

  deliveriesFilterThisWeek(deliveries: Delivery[]) {
    const start = moment().startOf('day').toDate().getTime();
    const end = moment().endOf('week').toDate().getTime();
    return deliveries.filter((delivery) => {
      const firstOn = new Date(delivery.due).getTime();
      return firstOn > start && firstOn < end;
    });
  }

  getPendingPayments(type: string) {
    return this.http.get<PendingPaymentLineItem[]>(
      `dashboard/pending-payment/${type}`
    );
  }

  getActiveDockets() {
    return this.http.get<ActiveDocket[]>(`dashboard/active`);
  }
}

export interface ActiveDocket {
  docket_id: number;
  invoice_number: number;
  categoryColor: string;
  item_name: string;
  product_id: number;
  quantity: number;
  on_hire: string;
  estimated_off_hire: string;
  customer: {
    id: number;
    full_name: string;
  };
  location_out: number;
  user_id: number;
}
